/**
 * Enum de permissões do sistema
 * Mantém sincronizado com o backend
 */
export enum Permission {
  // Assets
  ASSETS_CREATE = 'assets.create',
  ASSETS_EDIT = 'assets.edit',
  ASSETS_VIEW = 'assets.view',
  ASSETS_COMMANDS = 'assets.commands',
  ASSETS_REMOTE_CONTROL = 'assets.remote_control', // Acesso remoto e ações de energia/sessão

  // Requests
  REQUESTS_CREATE = 'requests.create',
  REQUESTS_APPROVE = 'requests.approve',
  REQUESTS_VIEW_ALL = 'requests.view_all',

  // Realtime
  REALTIME_VIEW = 'realtime.view',

  // Users
  USERS_CREATE = 'users.create',
  USERS_MANAGE = 'users.manage',

  // Employees
  EMPLOYEES_VIEW = 'employees.view',
  EMPLOYEES_MANAGE = 'employees.manage',
  CONTRACTS_VIEW = 'contracts.view',
  CONTRACTS_MANAGE = 'contracts.manage',
  CONTRACTS_CONFIG = 'contracts.config',

  // Settings
  SETTINGS_EDIT_NOTIFICATIONS = 'settings.edit_notifications',

  // Passwords
  PASSWORDS_VIEW_ALL = 'passwords.view_all',
  PASSWORDS_EDIT = 'passwords.edit',
  PASSWORDS_DELETE = 'passwords.delete',

  // Teams
  TEAMS_MANAGE = 'teams.manage',

  // Admin
  ADMIN_TENANT = 'admin.tenant',
  ADMIN_SYSTEM = 'admin.system',

  // Notion/Kanban
  NOTION_VIEW_KANBAN = 'notion.view_kanban', // Ver o kanban (todos os usuários logados)
  NOTION_MY_QUEUE = 'notion.my_queue', // Ver própria fila na dashboard
  NOTION_MANAGE_TASKS = 'notion.manage_tasks', // Gestão de tarefas
  NOTION_CONFIG = 'notion.config', // Configurar integração (apenas admin)
  NOTION_MANAGE_BOARDS = 'notion.manage_boards', // Gerenciar boards (apenas admin)
  NOTION_TRIGGER_SYNC = 'notion.trigger_sync', // Forçar sincronização manual
  NOTEBOOK_VIEW = 'notion.notebook_view', // Acessar Notebook do Cliente (apenas boards atribuídos)

  // Calendar
  CALENDAR_VIEW = 'calendar.view', // Visualizar calendário
  CALENDAR_CONFIG = 'calendar.config', // Configurar integração com Google Calendar (admin)
  CAPTATION_APPROVE = 'captation.approve', // Aprovar solicitações de captação

  // Dashboard
  DASHBOARD_ADMIN = 'dashboard.admin', // Ver dashboard administrativa
  ADMIN_TASKS_BOARD = 'admin.tasks_board', // Acessar Quadro Administrativo de Tarefas
  TEAM_CALENDAR_READ = 'team_calendar.read', // Visualizar calendário da equipe
  TEAM_CALENDAR_WRITE = 'team_calendar.write', // Criar/editar calendário da equipe

  // Mattermost
  MATTERMOST_CONFIG = 'mattermost.config', // Configurar integração com Mattermost
  MATTERMOST_SEND_MESSAGE = 'mattermost.send_message', // Enviar mensagens via Mattermost

  // Desktop Versions
  DESKTOP_VERSIONS_MANAGE = 'desktop.versions.manage', // Gerenciar versões do aplicativo desktop

  // Invoices
  INVOICES_SUBMIT = 'invoices.submit', // Enviar notas fiscais (prestadores de serviço)
  INVOICES_MANAGE = 'invoices.manage', // Gerenciar notas fiscais e faturamento (admin)

  // Roles
  ROLES_MANAGE = 'roles.manage', // Gerenciar roles e permissões

  // Documents
  DOCUMENTS_MANAGE = 'documents.manage', // Gerenciar documentos

  // Mattermost Image Validation
  MATTERMOST_IMAGE_VALIDATION = 'mattermost.image_validation', // Validar imagens do Mattermost

  // Certification / Cursos
  CERTIFICATION_VIEW = 'certification.view', // Área de cursos
  CERTIFICATION_MANAGE = 'certification.manage', // Gestão de cursos

  // Companies
  COMPANIES_MANAGE = 'companies.manage', // Gerenciar empresas

  // Payments
  PAYMENTS_MANAGE = 'payments.manage', // Gerenciar pagamentos de funcionários

  // AI Analysis (tokens e prompts)
  AI_ANALYSIS_MANAGE = 'ai.analysis_manage', // Gerenciar análises IA, uso de tokens e prompts

  // Reimbursements
  REIMBURSEMENTS_CREATE = 'reimbursements.create',
  REIMBURSEMENTS_VIEW_OWN = 'reimbursements.view_own',
  REIMBURSEMENTS_EDIT_OWN = 'reimbursements.edit_own',
  REIMBURSEMENTS_DELETE_OWN = 'reimbursements.delete_own',
  REIMBURSEMENTS_ADMIN = 'reimbursements.admin',
  REIMBURSEMENTS_APPROVE = 'reimbursements.approve',
  REIMBURSEMENTS_MANAGE_PACKAGES = 'reimbursements.manage_packages',
  REIMBURSEMENTS_REOPEN_PACKAGES = 'reimbursements.reopen_packages',
  REIMBURSEMENTS_VIEW_ANALYTICS = 'reimbursements.view_analytics',

  // Recurring Client Tasks (planejamentos mensais por cliente)
  RECURRING_CLIENT_TASKS_VIEW = 'recurring_client_tasks.view',
  RECURRING_CLIENT_TASKS_ADMIN = 'recurring_client_tasks.admin',

  // Store / Gamification Admin
  STORE_MANAGE = 'store.manage',

  // Tech Systems
  TECH_SYSTEMS_VIEW = 'tech_systems.view',
  TECH_SYSTEMS_MANAGE = 'tech_systems.manage',
  TECH_SYSTEMS_CONFIG = 'tech_systems.config',
  TECH_SYSTEMS_PROJECTS_VIEW = 'tech_systems.projects.view',

  // Meetings
  MEETINGS_VIEW = 'meetings.view',
  MEETINGS_MANAGE = 'meetings.manage',

  // Clients (CRM)
  CLIENTS_VIEW = 'clients.view', // Visualizar clientes
  CLIENTS_MANAGE = 'clients.manage', // Gerenciar clientes (criar, editar, excluir)
  CLIENTS_BOARD = 'clients.board', // Visualizar quadro de clientes por atendimento
  MY_CLIENTS_MANAGE_ALL = 'my_clients.manage_all', // Ver e gerenciar todos em Meus Clientes

  // Client Contracts (CRM)
  CLIENT_CONTRACTS_VIEW = 'client_contracts.view', // Visualizar contratos de clientes
  CLIENT_CONTRACTS_MANAGE = 'client_contracts.manage', // Gerenciar contratos de clientes

  // Company Documents
  COMPANY_DOCUMENTS_VIEW = 'company_documents.view', // Visualizar documentos da empresa
  COMPANY_DOCUMENTS_MANAGE = 'company_documents.manage', // Gerenciar documentos da empresa

  // Client Invoices (CRM)
  CLIENT_INVOICES_VIEW = 'client_invoices.view', // Visualizar notas fiscais de clientes
  CLIENT_INVOICES_MANAGE = 'client_invoices.manage', // Gerenciar notas fiscais de clientes

  // Admin / Audit
  ADMIN_AUDIT_LOGS_VIEW = 'admin.audit_logs.view',

  // Playbooks
  PLAYBOOKS_VIEW = 'playbooks.view',
  PLAYBOOKS_CREATE = 'playbooks.create',
  PLAYBOOKS_ADMIN = 'playbooks.admin',
  PLAYBOOKS_CONFIGURE = 'playbooks.configure',
  PLAYBOOKS_TRIGGERS = 'playbooks.triggers',

  // Drive Permissions Manager
  DRIVE_MANAGER_MANAGE = 'drive.manager.manage',

  // CNPJ Search
  CNPJ_SEARCH_VIEW = 'cnpj_search.view',

  // Mind Maps
  MIND_MAPS_VIEW = 'mind_maps.view',

  // Pipeline CRM
  PIPELINE_VIEW = 'pipeline.view',
  PIPELINE_MANAGE_LEADS = 'pipeline.manage_leads',
  PIPELINE_MANAGE_ASSETS = 'pipeline.manage_assets',
  PIPELINE_USE_AI = 'pipeline.use_ai',
}

export const PERMISSIONS_LIST = Object.values(Permission);

export const PERMISSIONS_DESCRIPTIONS: Record<Permission, string> = {
  [Permission.ASSETS_CREATE]: 'Criar itens no estoque',
  [Permission.ASSETS_EDIT]: 'Editar itens',
  [Permission.ASSETS_VIEW]: 'Visualizar estoque',
  [Permission.ASSETS_COMMANDS]:
    'Executar comandos remotos nos equipamentos (PowerShell/cmd) e ver as respostas',
  [Permission.ASSETS_REMOTE_CONTROL]: 'Acesso remoto e ações de energia/sessão nos PCs',
  [Permission.REQUESTS_CREATE]: 'Solicitar retirada',
  [Permission.REQUESTS_APPROVE]: 'Aprovar retiradas',
  [Permission.REQUESTS_VIEW_ALL]: 'Ver todas as solicitações',
  [Permission.REALTIME_VIEW]: 'Ver dashboard realtime',
  [Permission.USERS_CREATE]: 'Criar usuários',
  [Permission.USERS_MANAGE]: 'Gerenciar usuários e permissões',
  [Permission.EMPLOYEES_VIEW]: 'Visualizar funcionários',
  [Permission.EMPLOYEES_MANAGE]: 'Gerenciar funcionários',
  [Permission.CONTRACTS_VIEW]: 'Visualizar contratos',
  [Permission.CONTRACTS_MANAGE]: 'Gerenciar contratos',
  [Permission.CONTRACTS_CONFIG]: 'Configurar notificações de contratos',
  [Permission.SETTINGS_EDIT_NOTIFICATIONS]: 'Editar emails de alerta',
  [Permission.PASSWORDS_VIEW_ALL]: 'Visualizar todas as senhas',
  [Permission.PASSWORDS_EDIT]: 'Editar senhas',
  [Permission.PASSWORDS_DELETE]: 'Apagar senhas',
  [Permission.TEAMS_MANAGE]: 'Gerenciar equipes',
  [Permission.ADMIN_TENANT]: 'Administrador do Tenant (gerencia todas as permissões do tenant)',
  [Permission.ADMIN_SYSTEM]: 'Administrador do Sistema',
  [Permission.NOTION_VIEW_KANBAN]: 'Visualizar kanban do Notion',
  [Permission.NOTION_MY_QUEUE]: 'Ver própria fila na dashboard',
  [Permission.NOTION_MANAGE_TASKS]: 'Gestão de tarefas',
  [Permission.NOTION_CONFIG]: 'Configurar integração com Notion (admin)',
  [Permission.NOTION_MANAGE_BOARDS]: 'Gerenciar boards do Notion',
  [Permission.NOTION_TRIGGER_SYNC]: 'Forçar sincronização manual',
  [Permission.NOTEBOOK_VIEW]: 'Acessar Notebook do Cliente (boards atribuídos)',
  [Permission.CALENDAR_VIEW]: 'Visualizar calendário',
  [Permission.CALENDAR_CONFIG]:
    'Configurar integração com Google Calendar (admin)',
  [Permission.CAPTATION_APPROVE]: 'Aprovar solicitações de captação',
  [Permission.DASHBOARD_ADMIN]: 'Ver dashboard administrativa',
  [Permission.ADMIN_TASKS_BOARD]: 'Acessar Quadro Administrativo de Tarefas',
  [Permission.TEAM_CALENDAR_READ]: 'Visualizar calendário da equipe',
  [Permission.TEAM_CALENDAR_WRITE]:
    'Criar e gerenciar tarefas do calendário da equipe',
  [Permission.MATTERMOST_CONFIG]: 'Configurar integração com Mattermost',
  [Permission.MATTERMOST_SEND_MESSAGE]: 'Enviar mensagens via Mattermost',
  [Permission.DESKTOP_VERSIONS_MANAGE]: 'Gerenciar versões do aplicativo desktop',
  [Permission.INVOICES_SUBMIT]: 'Enviar notas fiscais (prestadores de serviço)',
  [Permission.INVOICES_MANAGE]: 'Gerenciar notas fiscais e faturamento (admin)',
  [Permission.ROLES_MANAGE]: 'Gerenciar roles e permissões',
  [Permission.DOCUMENTS_MANAGE]: 'Gerenciar documentos',
  [Permission.MATTERMOST_IMAGE_VALIDATION]: 'Validar imagens do Mattermost',
  [Permission.CERTIFICATION_VIEW]:
    'Área de cursos (ver cursos, fazer curso, simulado, certificado)',
  [Permission.CERTIFICATION_MANAGE]:
    'Gestão de cursos (criar/editar cursos, etapas, blocos, simulado, publicar)',
  [Permission.COMPANIES_MANAGE]: 'Gerenciar empresas',
  [Permission.PAYMENTS_MANAGE]: 'Gerenciar pagamentos de funcionários',
  [Permission.AI_ANALYSIS_MANAGE]: 'Gerenciar análises IA, uso de tokens e prompts',
  [Permission.REIMBURSEMENTS_CREATE]: 'Criar pedidos de reembolso',
  [Permission.REIMBURSEMENTS_VIEW_OWN]: 'Ver próprios reembolsos',
  [Permission.REIMBURSEMENTS_EDIT_OWN]: 'Editar próprios reembolsos',
  [Permission.REIMBURSEMENTS_DELETE_OWN]: 'Excluir próprios reembolsos',
  [Permission.REIMBURSEMENTS_ADMIN]: 'Administrar todos os reembolsos',
  [Permission.REIMBURSEMENTS_APPROVE]: 'Aprovar/reprovar reembolsos',
  [Permission.REIMBURSEMENTS_MANAGE_PACKAGES]: 'Gerenciar pacotes de reembolso',
  [Permission.REIMBURSEMENTS_REOPEN_PACKAGES]: 'Reabrir pacotes fechados',
  [Permission.REIMBURSEMENTS_VIEW_ANALYTICS]: 'Ver analytics de reembolsos',
  [Permission.RECURRING_CLIENT_TASKS_VIEW]:
    'Ver e concluir tarefas recorrentes de clientes sob responsabilidade',
  [Permission.RECURRING_CLIENT_TASKS_ADMIN]:
    'Gerenciar templates de tarefas recorrentes e visualização administrativa',
  [Permission.STORE_MANAGE]:
    'Gerenciar Loja de Pontos, Eventos Globais e Objetivos de Gamificação',
  [Permission.TECH_SYSTEMS_VIEW]:
    'Visualizar Sistemas TEC (sistemas em que é membro)',
  [Permission.TECH_SYSTEMS_MANAGE]:
    'Gerenciar todos os Sistemas TEC e membros de acesso',
  [Permission.TECH_SYSTEMS_CONFIG]:
    'Configurar módulo Sistemas TEC (ping global, GitHub, financeiro)',
  [Permission.TECH_SYSTEMS_PROJECTS_VIEW]:
    'Acessar Gestão de projetos dos Sistemas TEC',
  [Permission.MEETINGS_VIEW]: 'Visualizar reuniões',
  [Permission.MEETINGS_MANAGE]:
    'Gerenciar reuniões (criar, editar, cancelar)',
  [Permission.CLIENTS_VIEW]: 'Visualizar clientes',
  [Permission.CLIENTS_MANAGE]: 'Gerenciar clientes (criar, editar, excluir)',
  [Permission.CLIENTS_BOARD]: 'Visualizar quadro de clientes por atendimento',
  [Permission.MY_CLIENTS_MANAGE_ALL]:
    'Ver e gerenciar todos os clientes em Meus Clientes',
  [Permission.CLIENT_CONTRACTS_VIEW]: 'Visualizar contratos de clientes',
  [Permission.CLIENT_CONTRACTS_MANAGE]: 'Gerenciar contratos de clientes',
  [Permission.COMPANY_DOCUMENTS_VIEW]: 'Visualizar documentos da empresa',
  [Permission.COMPANY_DOCUMENTS_MANAGE]:
    'Gerenciar documentos da empresa (cadastrar, renovar, excluir, categorias)',
  [Permission.CLIENT_INVOICES_VIEW]: 'Visualizar notas fiscais de clientes',
  [Permission.CLIENT_INVOICES_MANAGE]: 'Gerenciar notas fiscais de clientes',
  [Permission.ADMIN_AUDIT_LOGS_VIEW]: 'Visualizar logs de auditoria',
  [Permission.PLAYBOOKS_VIEW]: 'Visualizar playbooks e etapas atribuídas',
  [Permission.PLAYBOOKS_CREATE]: 'Criar playbooks',
  [Permission.PLAYBOOKS_ADMIN]:
    'Administrar todos os playbooks (privados, reordenar, pular)',
  [Permission.PLAYBOOKS_CONFIGURE]:
    'Configurar templates, tipos de processo e alertas',
  [Permission.PLAYBOOKS_TRIGGERS]:
    'Configurar gatilhos de acionamento de playbooks',
  [Permission.DRIVE_MANAGER_MANAGE]:
    'Gerenciar pastas monitoradas e permissões do Google Drive',
  [Permission.CNPJ_SEARCH_VIEW]: 'Usar o Buscador de CNPJ',
  [Permission.MIND_MAPS_VIEW]: 'Usar Mapas Mentais',
  [Permission.PIPELINE_VIEW]: 'Visualizar Pipeline de Vendas (dashboard, leads, assets)',
  [Permission.PIPELINE_MANAGE_LEADS]: 'Criar, editar e excluir leads e interações',
  [Permission.PIPELINE_MANAGE_ASSETS]: 'Criar, editar e excluir assets de vendas',
  [Permission.PIPELINE_USE_AI]: 'Usar IA para extrair dados de leads',
};
